<template>
  <div :class="{'date-picker-wrapper': iconMode}">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ props }">
        <!-- Icon mode -->
        <template v-if="iconMode">
          <!-- No date selected - show icon only -->
          <v-tooltip
            v-if="!hasDateRange"
            :text="iconModeTooltip || placeholder || 'Select date range'"
            location="top"
          >
            <template v-slot:activator="{ props: tooltipProps }">
              <v-btn
                v-bind="{ ...props, ...tooltipProps }"
                icon="mdi-calendar-range"
                size="small"
                variant="text"
              ></v-btn>
            </template>
          </v-tooltip>
          <!-- Date selected - show date range in pill -->
          <v-chip
            v-else
            v-bind="props"
            class="date-range-pill"
            variant="outlined"
            size="small"
            :height="32"
            closable
            @click:close="clearDates"
          >
            <v-icon
              start
              size="18"
            >mdi-calendar-range</v-icon>
            {{ displayValue }}
          </v-chip>
        </template>
        <!-- Regular text field mode -->
        <v-text-field
          v-else
          v-bind="props"
          :model-value="displayValue"
          :label="label"
          :placeholder="placeholder"
          prepend-inner-icon="mdi-calendar-range"
          readonly
          variant="outlined"
          density="compact"
          hide-details
          clearable
          @click:clear="clearDates"
        ></v-text-field>
      </template>
      <v-card width="auto">
        <v-card-text class="pa-0">
        <div class="date-range-header">
          <div class="text-subtitle-1 font-weight-medium">
            {{ headerText }}
          </div>
        </div>
        <div class="date-range-calendars">
          <div class="date-range-calendar">
            <div class="calendar-navigation">
              <div class="calendar-nav-buttons">
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click="previousYear"
                >
                  <v-icon>mdi-chevron-double-left</v-icon>
                </v-btn>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click="previousMonth"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
              </div>
              <div class="calendar-month-year">
                <span class="calendar-month">{{ leftMonthName }}</span>
                <span class="calendar-year">{{ leftYear }}</span>
              </div>
              <div style="width: 72px"></div>
            </div>
            <div class="calendar-weekdays">
              <div v-for="day in weekDays" :key="day" class="weekday">
                {{ day }}
              </div>
            </div>
            <div class="calendar-days">
              <div
                v-for="(day, index) in leftMonthDays"
                :key="`left-${index}`"
                class="calendar-day"
                :class="getDayClasses(day, 'left')"
                @click="selectDate(day)"
                @mouseenter="handleMouseEnter(day)"
              >
                <span v-if="day">{{ day.getDate() }}</span>
              </div>
            </div>
          </div>

          <div class="date-range-calendar">
            <div class="calendar-navigation">
              <div style="width: 72px"></div>
              <div class="calendar-month-year">
                <span class="calendar-month">{{ rightMonthName }}</span>
                <span class="calendar-year">{{ rightYear }}</span>
              </div>
              <div class="calendar-nav-buttons">
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click="nextMonth"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click="nextYear"
                >
                  <v-icon>mdi-chevron-double-right</v-icon>
                </v-btn>
              </div>
            </div>
            <div class="calendar-weekdays">
              <div v-for="day in weekDays" :key="day" class="weekday">
                {{ day }}
              </div>
            </div>
            <div class="calendar-days">
              <div
                v-for="(day, index) in rightMonthDays"
                :key="`right-${index}`"
                class="calendar-day"
                :class="getDayClasses(day, 'right')"
                @click="selectDate(day)"
                @mouseenter="handleMouseEnter(day)"
              >
                <span v-if="day">{{ day.getDate() }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <div class="preset-buttons">
          <!-- <v-btn variant="text" size="small" @click="selectPreset('today')">Today</v-btn>
          <v-btn variant="text" size="small" @click="selectPreset('yesterday')">Yesterday</v-btn> -->
          <v-btn variant="text" size="small" @click="selectPreset('last7')">Last 7 days</v-btn>
          <v-btn variant="text" size="small" @click="selectPreset('last30')">Last 30 days</v-btn>
          <v-btn variant="text" size="small" @click="selectPreset('thisMonth')">This month</v-btn>
          <v-btn variant="text" size="small" @click="selectPreset('lastMonth')">Last month</v-btn>
        </div>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="cancel">Cancel</v-btn>
        <v-btn variant="text" @click="deselect">Deselect</v-btn>
      </v-card-actions>
    </v-card>
    </v-menu>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'VuetifyDateRangePicker',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: 'Date Range'
    },
    placeholder: {
      type: String,
      default: 'Select date range'
    },
    format: {
      type: String,
      default: 'MMM DD, YYYY'
    },
    disableFuture: {
      type: Boolean,
      default: false
    },
    iconMode: {
      type: Boolean,
      default: false
    },
    iconModeTooltip: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const menu = ref(false);
    const dateRange = ref([]);
    const tempStartDate = ref(null);
    const tempEndDate = ref(null);
    const hoverDate = ref(null);
    const selecting = ref(false);

    // Calendar navigation
    const today = new Date();
    const leftMonth = ref(today.getMonth());
    const leftYear = ref(today.getFullYear());
    const rightMonth = ref((today.getMonth() + 1) % 12);
    const rightYear = ref(today.getMonth() === 11 ? today.getFullYear() + 1 : today.getFullYear());

    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];

    // Initialize dates from modelValue
    watch(() => props.modelValue, (newVal) => {
      if (Array.isArray(newVal) && newVal.length === 2) {
        dateRange.value = [...newVal];
        tempStartDate.value = new Date(newVal[0]);
        tempEndDate.value = new Date(newVal[1]);
      } else {
        dateRange.value = [];
        tempStartDate.value = null;
        tempEndDate.value = null;
      }
    }, { immediate: true, deep: true });

    const displayValue = computed(() => {
      if (dateRange.value.length === 2 && dateRange.value[0] && dateRange.value[1]
      ) {
        const start = formatDate(new Date(dateRange.value[0]));
        const end = formatDate(new Date(dateRange.value[1]));
        return `${start} - ${end}`;
      }
      return '';
    });

    const headerText = computed(() => {
      if (tempStartDate.value && tempEndDate.value) {
        const start = formatDate(tempStartDate.value);
        const end = formatDate(tempEndDate.value);
        return `${start} - ${end}`;
      } else if (tempStartDate.value) {
        return `${formatDate(tempStartDate.value)} - Select end date`;
      }
      return 'Select date range';
    });

    const leftMonthName = computed(() => monthNames[leftMonth.value]);
    const rightMonthName = computed(() => monthNames[rightMonth.value]);

    const isValidRange = computed(() => {
      return tempStartDate.value && tempEndDate.value;
    });

    const hasDateRange = computed(() => {
      return dateRange.value.length === 2;
    });

    const formatDate = (date) => {
      if (!date) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    };

    const getMonthDays = (month, year) => {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const days = [];

      // Add empty days for alignment
      for (let i = 0; i < firstDay.getDay(); i++) {
        days.push(null);
      }

      // Add actual days
      for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push(new Date(year, month, i));
      }
      
      // Fill to complete weeks
      while (days.length % 7 !== 0) {
        days.push(null);
      }
      
      return days;
    };

    const leftMonthDays = computed(() => getMonthDays(leftMonth.value, leftYear.value));
    const rightMonthDays = computed(() => getMonthDays(rightMonth.value, rightYear.value));

    const isDateDisabled = (date) => {
      if (!date) return true;
      if (props.disableFuture && date > today) return true;
      return false;
    };

    const isDateInRange = (date) => {
      if (!date || !tempStartDate.value) return false;
      
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      
      if (tempEndDate.value) {
        const start = new Date(tempStartDate.value);
        const end = new Date(tempEndDate.value);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        return compareDate >= start && compareDate <= end;
      } else if (selecting.value && hoverDate.value) {
        const start = new Date(tempStartDate.value);
        const hover = new Date(hoverDate.value);
        start.setHours(0, 0, 0, 0);
        hover.setHours(0, 0, 0, 0);
        
        if (hover >= start) {
          return compareDate >= start && compareDate <= hover;
        } else {
          return compareDate >= hover && compareDate <= start;
        }
      }
      
      return false;
    };

    const isDateSelected = (date) => {
      if (!date) return false;
      
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      
      if (tempStartDate.value) {
        const start = new Date(tempStartDate.value);
        start.setHours(0, 0, 0, 0);
        if (compareDate.getTime() === start.getTime()) return true;
      }
      
      if (tempEndDate.value) {
        const end = new Date(tempEndDate.value);
        end.setHours(0, 0, 0, 0);
        if (compareDate.getTime() === end.getTime()) return true;
      }
      
      return false;
    };

    const getDayClasses = (day, calendar) => {
      if (!day) return 'empty';
      
      const classes = [];
      const isDisabled = isDateDisabled(day);
      const isSelected = isDateSelected(day);
      const isInRange = isDateInRange(day);
      const isToday = isSameDay(day, today);
      
      if (isDisabled) classes.push('disabled');
      if (isSelected) classes.push('selected');
      if (isInRange && !isSelected) classes.push('in-range');
      if (isToday) classes.push('today');
      
      // Add range start/end classes
      if (tempStartDate.value && isSameDay(day, tempStartDate.value)) {
        classes.push('range-start');
      }
      if (tempEndDate.value && isSameDay(day, tempEndDate.value)) {
        classes.push('range-end');
      } else if (selecting.value && hoverDate.value && isSameDay(day, hoverDate.value)) {
        classes.push('range-end');
      }
      
      return classes.join(' ');
    };

    const isSameDay = (date1, date2) => {
      if (!date1 || !date2) return false;
      return date1.getFullYear() === date2.getFullYear() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate();
    };

    const selectDate = (date) => {
      if (!date || isDateDisabled(date)) return;

      if (!tempStartDate.value || (tempStartDate.value && tempEndDate.value)) {
        // Start new selection
        tempStartDate.value = new Date(date);
        tempEndDate.value = null;
        selecting.value = true;
      } else {
        // Complete selection
        const newDate = new Date(date);
        if (newDate >= tempStartDate.value) {
          tempEndDate.value = newDate;
        } else {
          tempEndDate.value = tempStartDate.value;
          tempStartDate.value = newDate;
        }
        selecting.value = false;
        // Auto-apply when both dates are selected
        apply();
      }
    };

    const handleMouseEnter = (date) => {
      if (selecting.value && date && !isDateDisabled(date)) {
        hoverDate.value = date;
      }
    };

    const previousMonth = () => {
      if (leftMonth.value === 0) {
        leftMonth.value = 11;
        leftYear.value--;
      } else {
        leftMonth.value--;
      }

      if (rightMonth.value === 0) {
        rightMonth.value = 11;
        rightYear.value--;
      } else {
        rightMonth.value--;
      }
    };

    const nextMonth = () => {
      if (leftMonth.value === 11) {
        leftMonth.value = 0;
        leftYear.value++;
      } else {
        leftMonth.value++;
      }

      if (rightMonth.value === 11) {
        rightMonth.value = 0;
        rightYear.value++;
      } else {
        rightMonth.value++;
      }
    };

    const previousYear = () => {
      leftYear.value--;
      rightYear.value--;
    };

    const nextYear = () => {
      leftYear.value++;
      rightYear.value++;
    };

    const selectPreset = (preset) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let start, end;

      switch (preset) {
        case 'today':
          start = new Date(today);
          end = new Date(today);
          break;
        case 'yesterday':
          start = new Date(today);
          start.setDate(start.getDate() - 1);
          end = new Date(start);
          break;
        case 'last7':
          end = new Date(today);
          start = new Date(today);
          start.setDate(start.getDate() - 6);
          break;
        case 'last30':
          end = new Date(today);
          start = new Date(today);
          start.setDate(start.getDate() - 29);
          break;
        case 'thisMonth':
          start = new Date(today.getFullYear(), today.getMonth(), 1);
          end = new Date(today);
          break;
        case 'lastMonth':
          start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          end = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
      }

      tempStartDate.value = start;
      tempEndDate.value = end;
      selecting.value = false;
      // Auto-apply when preset is selected
      apply();
    };

    const apply = () => {
      if (tempStartDate.value && tempEndDate.value) {
        const start = tempStartDate.value//.toISOString().split('T')[0];
        const end = tempEndDate.value//.toISOString().split('T')[0];
        dateRange.value[0] = start;
        dateRange.value[1] = end;

        emit('update:modelValue', dateRange.value);
        emit('change', dateRange.value);
      }
      menu.value = false;
    };

    const cancel = () => {
      // Reset to original values
      if (dateRange.value.length === 2) {
        tempStartDate.value = new Date(dateRange.value[0]);
        tempEndDate.value = new Date(dateRange.value[1]);
      } else {
        tempStartDate.value = null;
        tempEndDate.value = null;
      }
      selecting.value = false;
      menu.value = false;
    };

    const clearDates = () => {
      dateRange.value = [];
      tempStartDate.value = null;
      tempEndDate.value = null;
      selecting.value = false;
      emit('update:modelValue', []);
      emit('change', []);
    };

    const deselect = () => {
      clearDates();
      menu.value = false;
    };

    return {
      menu,
      displayValue,
      headerText,
      isValidRange,
      selectPreset,
      apply,
      cancel,
      clearDates,
      leftMonth,
      leftYear,
      rightMonth,
      rightYear,
      leftMonthName,
      rightMonthName,
      weekDays,
      leftMonthDays,
      rightMonthDays,
      getDayClasses,
      selectDate,
      handleMouseEnter,
      previousMonth,
      nextMonth,
      previousYear,
      nextYear,
      deselect,
      hasDateRange
    };
  }
};
</script>

<style scoped>
.date-picker-wrapper {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  height: 40px;
}

.date-picker-wrapper .v-btn {
  margin: 0;
}

.date-range-pill {
  cursor: pointer;
  font-size: 0.875rem;
  padding-right: 12px !important;
  padding-left: 2px !important;
  background-color: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.04);
  display: inline-flex;
  align-items: center;
}

.date-range-pill:hover {
  background-color: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.08);
}

:deep(.v-chip__content) {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-range-header {
  padding: 16px;
  background-color: rgb(var(--v-theme-primary));
  color: white;
  text-align: center;
}

.date-range-calendars {
  display: flex;
  justify-content: center;
  background-color: white;
}

.date-range-calendar {
  padding: 16px;
  width: 280px;
}

.date-range-calendar + .date-range-calendar {
  border-left: 1px solid #e0e0e0;
}

.calendar-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.calendar-nav-buttons {
  display: flex;
  align-items: center;
}

.calendar-month-year {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
}

.calendar-month {
  font-weight: 500;
}

.calendar-year {
  color: #666;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  margin-bottom: 8px;
}

.weekday {
  text-align: center;
  font-size: 12px;
  color: #666;
  padding: 4px;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  position: relative;
  transition: all 0.2s ease;
}

.calendar-day.empty {
  cursor: default;
}

.calendar-day:not(.empty):not(.disabled):hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
  border-radius: 50%;
}

.calendar-day.disabled {
  color: #ccc;
  cursor: not-allowed;
}

.calendar-day.today {
  font-weight: 600;
}

.calendar-day.selected {
  background-color: rgb(var(--v-theme-primary));
  color: white;
  border-radius: 50%;
}

.calendar-day.in-range {
  background-color: rgba(var(--v-theme-primary), 0.12);
  border-radius: 0;
}

.calendar-day.range-start {
  background-color: rgb(var(--v-theme-primary));
  color: white;
  border-radius: 50% 0 0 50%;
}

.calendar-day.range-end {
  background-color: rgb(var(--v-theme-primary));
  color: white;
  border-radius: 0 50% 50% 0;
}

.calendar-day.range-start.range-end {
  border-radius: 50%;
}

.preset-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.preset-buttons .v-btn {
  text-transform: none;
}
</style>
